import { PDF_DOCUMENT_OPTIONS } from '@chiroup/components/pdfDocumentOptions';
import {
  fieldBackgroundCss,
  fieldBorderCss,
  isElementHiddenByMissingToken,
  isElementVisible,
  isoToMdy,
  mdyToIso,
} from '@chiroup/core/functions/CustomForm.helpers';
import { CustomFormElement, FormRenderMode } from '@chiroup/core/types/CustomForm.type';
import { ChevronLeftIcon, ChevronRightIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureFieldModal from './SignatureFieldModal';

// Bundle the pdf.js worker same-origin (Vite copies it from the installed
// pdfjs-dist) instead of a public CDN: a CDN outage / clinic CSP block would
// silently break overlay rendering, and we don't want uncontrolled external JS
// running in a page that holds PHI + the session.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

// Scratch-mode canonical page (96dpi US Letter), matching the builder + server PDF.
const SCRATCH_W = 816;
const SCRATCH_H = 1056;

const DEFAULT_OVERLAY_UNAVAILABLE =
  'This document is temporarily unavailable. Please try again later.';

export type CustomFormCanvasProps = {
  elements: CustomFormElement[];
  mode: FormRenderMode;
  basePdfUrl?: string | null;
  pageCount?: number | null;
  /** Final on-screen width of the page; the consumer computes this from its own
   * layout (container ResizeObserver vs window width) so each keeps its sizing. */
  renderWidth: number;
  fieldValues: Record<string, string>;
  /** Signature data URLs keyed by element id. The staff screen folds these into
   * fieldValues (the server renderer embeds the data:image value); the portal
   * tracks them in a separate map. */
  signatures: Record<string, string>;
  onChange: (id: string, value: string) => void;
  /** Called with the captured PNG data URL, or '' to clear the signature. */
  onSignatureChange: (id: string, dataUrl: string) => void;
  /** Staff (Generate) may edit auto-filled token values; the portal keeps them
   * read-only. */
  tokensEditable?: boolean;
  overlayUnavailableMessage?: string;
  onCaptureError?: (err: unknown) => void;
  /** Apply the per-element "Hide if Token Missing" rule (patient portal + staff
   * review, where fieldValues hold real resolved tokens). Off in design preview. */
  applyHideIfTokenMissing?: boolean;
  /** Brand color for the signature signing modal's Save button (portal passes the
   * clinic color; defaults to the app primary). */
  primaryColor?: string;
  /** Patient-fill surfaces set this so user-owned (staff-completed) fields render
   * read-only — the patient sees them filled but can't edit. Off for staff. */
  lockUserOwnedFields?: boolean;
};

/**
 * The single WYSIWYG positioned-form renderer shared by the staff Generate/Review
 * screen and the patient portal — the same layout the server renders to PDF. Owns
 * the white "paper" card, the react-pdf overlay, page navigation, the per-type
 * control switch, and the signature pads; the consuming screens own value state,
 * submission, and surrounding chrome. Centralizing this is what keeps the on-screen
 * renderers from drifting out of pixel-parity with each other and with the PDF.
 */
const CustomFormCanvas: React.FC<CustomFormCanvasProps> = ({
  elements,
  mode,
  basePdfUrl,
  pageCount,
  renderWidth,
  fieldValues,
  signatures,
  onChange,
  onSignatureChange,
  tokensEditable = false,
  overlayUnavailableMessage = DEFAULT_OVERLAY_UNAVAILABLE,
  onCaptureError,
  applyHideIfTokenMissing = false,
  primaryColor,
  lockUserOwnedFields = false,
}) => {
  // Overlay page count comes from the loaded PDF; scratch comes from pageCount
  // (derived below so it stays reactive when pageCount changes).
  const [pdfNumPages, setPdfNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // Id of the signature field whose signing modal is open (null = closed).
  const [signingElId, setSigningElId] = useState<string | null>(null);

  // Per-page natural size; clear on nav so overlays don't briefly use a stale size.
  useEffect(() => {
    setNatural(null);
  }, [currentPage]);

  const isOverlay = mode === 'pdf-overlay';
  const numPages = isOverlay ? pdfNumPages : pageCount || 1;
  const pageW = isOverlay ? (natural?.w ?? SCRATCH_W) : SCRATCH_W;
  const pageH = isOverlay ? (natural?.h ?? SCRATCH_H) : SCRATCH_H;
  const scale = pageW > 0 ? renderWidth / pageW : 1;
  const overlayUnavailable = isOverlay && !basePdfUrl;

  const pageElements = useMemo(() => {
    // Token-id-keyed values for the hide-if-missing rule (only built when applied):
    // seeded patient/insurance values plus each token element's own resolved value.
    const tokenValues: Record<string, string> = {};
    if (applyHideIfTokenMissing) {
      for (const [k, v] of Object.entries(fieldValues)) {
        if (k.includes('.')) {
          tokenValues[k] = v;
        }
      }
      for (const el of elements) {
        if (el.type === 'token' && el.tokenId) {
          tokenValues[el.tokenId] = fieldValues[el.id] ?? tokenValues[el.tokenId] ?? '';
        }
      }
    }
    return elements.filter(
      e =>
        (e.page || 1) === currentPage &&
        isElementVisible(e, fieldValues) &&
        !(applyHideIfTokenMissing && isElementHiddenByMissingToken(e, tokenValues))
    );
  }, [elements, currentPage, fieldValues, applyHideIfTokenMissing]);

  const renderControl = (el: CustomFormElement) => {
    const value = fieldValues[el.id] ?? '';
    // Size control text to the height the control ACTUALLY gets (the box height
    // minus the label row when the label sits above it), then scale with the page.
    // A flat 14px — even scaled — plus the native control's own padding overflows a
    // short field (e.g. a 36px dropdown), clipping the selected value to an
    // empty-looking sliver. Tying the font to the available height keeps the value
    // visible at any size, capped so tall fields don't balloon and floored to stay
    // legible. Mirrors the PDF renderer (size ∝ box height).
    const STACKED_LABEL = [
      'singleLine',
      'multiLine',
      'token',
      'date',
      'dropdown',
      'number',
      'radio',
    ];
    const labelBand = el.label && STACKED_LABEL.includes(el.type) ? 15 : 0;
    const controlH = Math.max(el.h - labelBand, 12);
    // No explicit line-height: a native <select> clips the TOP of its value when
    // given a line-height tighter than its intrinsic one (label + inputs look fine,
    // the select doesn't). Let each control use its default vertical centering.
    const ctrlStyle: React.CSSProperties = {
      fontSize: Math.max(Math.min(controlH * 0.55, 14), 7) * scale,
    };
    const labelStyle: React.CSSProperties = { fontSize: 11 * scale };
    const borderStyle = {
      ...fieldBorderCss(el.border),
      ...fieldBackgroundCss(el.fill),
      ...ctrlStyle,
    } as React.CSSProperties;

    const fieldLabel = el.label ? (
      <span className="break-words font-medium text-gray-600" style={labelStyle}>
        {el.label}
        {el.required ? <span className="text-red-500"> *</span> : null}
      </span>
    ) : null;

    switch (el.type) {
      case 'staticText':
        return (
          <div
            className="h-full w-full overflow-hidden whitespace-pre-wrap break-words"
            style={{
              fontSize: (el.formatting?.fontSize ?? 12) * scale,
              fontWeight: el.formatting?.bold ? 700 : undefined,
              fontStyle: el.formatting?.italic ? 'italic' : undefined,
              textAlign: el.formatting?.align || 'left',
            }}
          >
            {fieldValues[el.id] ?? el.text ?? ''}
          </div>
        );
      case 'sectionDivider':
        return (
          <div className="flex h-full w-full items-center gap-2">
            {el.label ? (
              <span
                className="shrink-0 font-semibold text-gray-500"
                style={{ fontSize: 12 * scale }}
              >
                {el.label}
              </span>
            ) : null}
            <span className="h-px flex-1 bg-gray-300" />
          </div>
        );
      case 'token': {
        // A token can resolve to an image (provider.signature is an S3-stored PNG
        // the resolver inlines as a data URL) — show it as a picture, never as the
        // raw data string, and keep it read-only even on the staff edit screen.
        const isImage = value.startsWith('data:image');
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {el.label ? (
              <span className="break-words font-medium text-gray-600" style={labelStyle}>
                {el.label}
              </span>
            ) : null}
            {isImage ? (
              <img
                src={value}
                alt={el.label || 'signature'}
                className="min-h-0 w-full flex-1 object-contain object-left"
              />
            ) : tokensEditable ? (
              // Multi-value tokens (e.g. several diagnoses) resolve with newline
              // separators — use a textarea so the line breaks are visible/editable;
              // single-value tokens keep the compact single-line input.
              value.includes('\n') ? (
                <textarea
                  value={value}
                  onChange={e => onChange(el.id, e.target.value)}
                  style={ctrlStyle}
                  className="min-h-0 w-full flex-1 resize-none rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
                />
              ) : (
                <input
                  value={value}
                  onChange={e => onChange(el.id, e.target.value)}
                  style={ctrlStyle}
                  className="min-h-0 w-full flex-1 rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
                />
              )
            ) : (
              <div
                className="flex min-h-0 flex-1 items-start overflow-y-auto rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-sm text-gray-700"
                style={borderStyle}
              >
                <span className="whitespace-pre-wrap break-words">{value || ''}</span>
              </div>
            )}
          </div>
        );
      }
      case 'multiLine':
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {fieldLabel}
            <textarea
              value={value}
              onChange={e => onChange(el.id, e.target.value)}
              style={borderStyle}
              className="min-h-0 w-full flex-1 resize-none rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
        );
      case 'date':
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {fieldLabel}
            <input
              type="date"
              value={mdyToIso(value)}
              onChange={e => onChange(el.id, isoToMdy(e.target.value))}
              style={borderStyle}
              className="min-h-0 w-full flex-1 rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
        );
      case 'singleLine':
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {fieldLabel}
            <input
              value={value}
              onChange={e => onChange(el.id, e.target.value)}
              placeholder={el.placeholder || ''}
              style={borderStyle}
              className="min-h-0 w-full flex-1 rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex h-full w-full items-center gap-2" style={ctrlStyle}>
            <input
              type="checkbox"
              checked={value === 'true'}
              onChange={e => onChange(el.id, e.target.checked ? 'true' : 'false')}
              className="shrink-0"
            />
            <span className="min-w-0 break-words">
              {el.label}
              {el.required ? <span className="text-red-500"> *</span> : null}
            </span>
          </label>
        );
      case 'radio': {
        // Size each option's TEXT to fit the available height so every option stays
        // visible on a short field. The radio control gets an EXPLICIT size (not the
        // inherited font-size): native radios scale with font-size, and an enlarged
        // control at the left edge was getting its left half clipped.
        const optCount = Math.max((el.options || []).length, 1);
        const optFont = Math.max(Math.min(controlH / optCount / 1.5, 13), 6.5) * scale;
        const dot = Math.min(optFont, 13 * scale);
        return (
          <div className="flex h-full w-full flex-col gap-1.5">
            {fieldLabel}
            {/* No overflow clip here: a selected radio's focus/accent ring sits just
                outside the control box and was getting shaved by overflow-auto. The
                per-option font auto-fit (above) keeps options within the field, so
                scrolling isn't needed. pl-2 keeps the control off the field edge. */}
            <div className="flex min-h-0 flex-1 flex-col gap-0.5 pl-2">
              {(el.options || []).map(opt => (
                <label
                  key={opt.id}
                  className="flex items-start gap-1.5"
                  style={{ fontSize: optFont }}
                >
                  <input
                    type="radio"
                    name={el.id}
                    checked={value === opt.id}
                    onChange={() => onChange(el.id, opt.id)}
                    className="mt-0.5 shrink-0"
                    style={{ width: dot, height: dot }}
                  />
                  <span className="min-w-0 break-words">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      }
      case 'signature': {
        // Signing happens in a larger pop-up pad (SignatureFieldModal) — the inline
        // box is a tap target that shows the captured signature (or a prompt). The
        // captured PNG is drawn stretched to this box + the PDF, so object-fill here
        // matches the output.
        const sigValue = signatures[el.id];
        const isSigned = !!sigValue && sigValue.startsWith('data:image');
        return (
          <div className="flex h-full w-full flex-col">
            {/* Label + required marker sit above the pad. With no label the required
                marker sits beside the pad (to the right) instead of on its own line
                above the field. */}
            {el.label || isSigned ? (
              <div className="flex items-center justify-between gap-2">
                <span
                  className="min-w-0 truncate uppercase tracking-wide text-gray-400"
                  style={{ fontSize: 10 * scale }}
                >
                  {el.label}
                  {el.label && el.required ? <span className="text-red-500"> *</span> : null}
                </span>
                {isSigned ? (
                  <button
                    type="button"
                    onClick={() => onSignatureChange(el.id, '')}
                    className="shrink-0 text-gray-500 underline"
                    style={{ fontSize: 10 * scale }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="flex min-h-0 flex-1 items-start gap-1">
              <button
                type="button"
                onClick={() => setSigningElId(el.id)}
                className="relative h-full min-h-0 flex-1 overflow-hidden rounded border border-gray-300 bg-white"
              >
                {isSigned ? (
                  <img
                    src={sigValue}
                    alt={el.label || 'signature'}
                    className="h-full w-full object-fill"
                  />
                ) : (
                  <span
                    className="flex h-full w-full items-center justify-center gap-1 text-gray-400"
                    style={{ fontSize: 11 * scale }}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Tap to sign
                  </span>
                )}
              </button>
              {!el.label && el.required ? (
                <span className="shrink-0 text-red-500" style={{ fontSize: 10 * scale }}>
                  *
                </span>
              ) : null}
            </div>
          </div>
        );
      }
      case 'dropdown':
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {fieldLabel}
            <select
              value={value}
              onChange={e => onChange(el.id, e.target.value)}
              style={borderStyle}
              className="min-h-0 w-full flex-1 rounded border border-gray-300 px-1 text-sm focus:border-primary-500 focus:outline-none"
            >
              <option value="">Select…</option>
              {(el.options || []).map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );
      case 'number':
        return (
          <div className="flex h-full w-full flex-col gap-0.5">
            {fieldLabel}
            {/* Currency shows a persistent "$" while filling (the value stays a raw
                number; the PDF renderer formats it as USD). */}
            <div className="relative flex min-h-0 flex-1">
              {el.currency ? (
                <span className="pointer-events-none absolute inset-y-0 left-1.5 flex items-center text-sm text-gray-500">
                  $
                </span>
              ) : null}
              <input
                value={value}
                inputMode="decimal"
                onChange={e => onChange(el.id, e.target.value)}
                placeholder={el.placeholder || (el.currency ? '0.00' : '')}
                style={borderStyle}
                className={`min-h-0 w-full flex-1 rounded border border-gray-300 text-sm focus:border-primary-500 focus:outline-none ${
                  el.currency ? 'pl-4 pr-1' : 'px-1'
                }`}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (overlayUnavailable) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-700">
        {overlayUnavailableMessage}
      </div>
    );
  }

  const signingEl = signingElId ? elements.find(e => e.id === signingElId) : null;

  return (
    <div className="flex flex-col items-center gap-3">
      {signingEl && (
        <SignatureFieldModal
          open
          label={signingEl.label}
          aspect={signingEl.h > 0 ? signingEl.w / signingEl.h : 3}
          primaryColor={primaryColor}
          onSave={dataUrl => {
            onSignatureChange(signingEl.id, dataUrl);
            setSigningElId(null);
          }}
          onClose={() => setSigningElId(null)}
          onCaptureError={onCaptureError}
        />
      )}
      <div
        className="relative shrink-0 bg-white text-gray-900 shadow ring-1 ring-gray-200"
        style={{
          width: pageW * scale,
          height: pageH * scale,
          touchAction: 'manipulation',
          // Card is always white "paper": force light scheme so native control
          // chrome (date input text + calendar icon, selects) stays dark-on-white
          // when the surrounding app is in dark mode.
          colorScheme: 'light',
        }}
      >
        {isOverlay && basePdfUrl && (
          <Document
            file={basePdfUrl}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={({ numPages: n }) => setPdfNumPages(n)}
            onLoadError={err => Sentry.captureException(err)}
          >
            <Page
              pageNumber={currentPage}
              width={renderWidth}
              rotate={0}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              onLoadSuccess={page => {
                const vp = page.getViewport({ scale: 1, rotation: 0 });
                setNatural({ w: vp.width, h: vp.height });
              }}
            />
          </Document>
        )}

        {pageElements.map(el => {
          // User-owned fields are completed by staff before the form is sent; on a
          // patient-fill surface they render read-only (inert blocks all interaction)
          // so the patient sees them filled but can't change them.
          const locked = lockUserOwnedFields && el.completedBy === 'user';
          return (
            <div
              key={el.id}
              className={locked ? 'absolute opacity-80' : 'absolute'}
              style={{
                left: el.x * scale,
                top: el.y * scale,
                width: el.w * scale,
                height: el.h * scale,
              }}
              inert={locked}
            >
              {renderControl(el)}
            </div>
          );
        })}
      </div>

      {numPages > 1 && (
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 disabled:opacity-40"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Page {currentPage} of {numPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
            disabled={currentPage >= numPages}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 disabled:opacity-40"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomFormCanvas;
